/**
 * /inventory/audits — packer shift stock-audit analytics.
 *
 * Each tab owns its own query. Radix `TabsContent` only mounts the active tab,
 * so opening the page costs one request instead of five.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { ScanLine } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Skeleton } from '@/components/ui/Skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { KPICard } from '@/components/charts/KPICard';
import { BarChart } from '@/components/charts/BarChart';
import { useFilters, rangeISO } from '@/state/filters';
import { openAuditDrilldown } from '@/state/auditDrilldownDialog';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';
import {
  useAuditsCoverage,
  useAuditsOverview,
  useAuditsPackers,
  useAuditsSessions,
  useAuditsVariance,
} from '@/api/bi/audits';

function pct(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return '—';
  return `${(v * 100).toFixed(1)}%`;
}

/**
 * Clickable table row. Keyboard-reachable, because a drill-down that only
 * responds to a mouse is a drill-down half the team cannot use.
 */
function DrillRow({
  onOpen,
  children,
  className,
}: {
  onOpen: () => void;
  children: ReactNode;
  className?: string;
}) {
  return (
    <tr
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      className={cn(
        'cursor-pointer border-t border-border transition-colors hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none',
        className,
      )}
    >
      {children}
    </tr>
  );
}

interface TabProps {
  filters: { start: string; end: string; storeId: number | null };
}

export function StockAuditPage() {
  const { range, storeId, timeRangeMode } = useFilters();
  const iso = useMemo(() => rangeISO(range, timeRangeMode), [range, timeRangeMode]);
  const filters = { start: iso.start, end: iso.end, storeId: storeId ?? null };

  return (
    <>
      <PageHeader
        eyebrow="Inventory"
        title="Stock audits"
        description="Packer shift audits enforced at logout. Signed variance is scanned − expected; overcount can include sold units."
      />

      <Tabs defaultValue="overview">
        <TabsList className="mb-3">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="variance">Variance</TabsTrigger>
          <TabsTrigger value="packers">Packers</TabsTrigger>
          <TabsTrigger value="coverage">Coverage</TabsTrigger>
          <TabsTrigger value="sessions">Sessions</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab filters={filters} />
        </TabsContent>
        <TabsContent value="variance">
          <VarianceTab filters={filters} />
        </TabsContent>
        <TabsContent value="packers">
          <PackersTab filters={filters} />
        </TabsContent>
        <TabsContent value="coverage">
          <CoverageTab filters={filters} />
        </TabsContent>
        <TabsContent value="sessions">
          <SessionsTab filters={filters} />
        </TabsContent>
      </Tabs>
    </>
  );
}

// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------

function OverviewTab({ filters }: TabProps) {
  const overview = useAuditsOverview(filters);
  const k = overview.data?.kpis;

  if (overview.isLoading) {
    return (
      <div className="grid gap-3 md:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-24" />
        ))}
      </div>
    );
  }
  if (overview.isError || !k) {
    return (
      <ErrorState
        description="Could not load audit overview."
        onRetry={() => void overview.refetch()}
      />
    );
  }

  return (
    <div className="space-y-4">
      {overview.data?.caveat ? (
        <p className="text-xs text-muted-foreground">{overview.data.caveat}</p>
      ) : null}
      <div className="grid gap-3 md:grid-cols-4">
        <KPICard
          label="Shift compliance"
          value={pct(k.shift_compliance)}
          hint={`${k.completed_sessions}/${k.expected_sessions} expected`}
        />
        <KPICard
          label="Completion rate"
          value={pct(k.completion_rate)}
          hint={`${k.completed_sessions} of ${k.sessions} sessions`}
        />
        <KPICard
          label="Shortfall units"
          value={fmt.number(k.shortfall_units)}
          hint={`${k.shortfall_lines} lines`}
        />
        <KPICard
          label="Overcount units"
          value={fmt.number(k.overcount_units)}
          hint={`${k.overcount_lines} lines (may include sold)`}
        />
      </div>
      <div className="grid gap-3 md:grid-cols-4">
        <KPICard
          label="Units counted"
          value={fmt.number(k.scanned_units)}
          hint={`${fmt.number(k.expected_units)} expected across all lines`}
        />
        <KPICard
          label="Count accuracy"
          value={pct(
            // Only meaningful against a non-zero expectation; an empty window
            // would otherwise read as a confident 0%.
            k.expected_units ? k.scanned_units / k.expected_units : null,
          )}
          hint="Scanned units as a share of expected"
        />
        <KPICard
          label="SKUs touched"
          value={fmt.number(k.skus_touched)}
          hint={`${fmt.number(k.lines)} audit lines`}
        />
        <KPICard
          label="Packers · stores"
          value={`${fmt.number(k.packers)} · ${fmt.number(k.stores)}`}
          hint="Distinct packers and stores auditing in this window"
        />
      </div>
      <div className="grid gap-3 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Sessions by day</CardTitle>
          </CardHeader>
          <CardBody>
            <BarChart
              data={(overview.data?.by_day ?? []).map((d) => ({
                name: d.day ?? '—',
                value: d.completed,
              }))}
              xKey="name"
              yKey="value"
            />
          </CardBody>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>By store</CardTitle>
          </CardHeader>
          <CardBody className="space-y-3">
            <BarChart
              data={(overview.data?.by_store ?? []).map((d) => ({
                name: d.store_name,
                value: d.completed,
              }))}
              xKey="name"
              yKey="value"
              horizontal
            />
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 text-[10px] uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-1.5 text-left">Store</th>
                    <th className="px-3 py-1.5 text-right">Audits</th>
                    <th className="px-3 py-1.5 text-right">Completed</th>
                  </tr>
                </thead>
                <tbody>
                  {(overview.data?.by_store ?? []).map((d) => (
                    <DrillRow
                      key={d.store_id}
                      onOpen={() =>
                        openAuditDrilldown({
                          kind: 'coverageStore',
                          storeId: d.store_id,
                          storeName: d.store_name,
                        })
                      }
                    >
                      <td className="px-3 py-1.5">{d.store_name}</td>
                      <td className="px-3 py-1.5 text-right">{d.audits}</td>
                      <td className="px-3 py-1.5 text-right">{d.completed}</td>
                    </DrillRow>
                  ))}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Variance
// ---------------------------------------------------------------------------

function VarianceTab({ filters }: TabProps) {
  const variance = useAuditsVariance(filters);

  if (variance.isLoading) return <Skeleton className="h-64" />;
  if (variance.isError || !variance.data) {
    return (
      <ErrorState
        description="Could not load variance."
        onRetry={() => void variance.refetch()}
      />
    );
  }

  const d = variance.data;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>By audit bucket</CardTitle>
        </CardHeader>
        <CardBody className="overflow-x-auto p-0">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 text-[10px] uppercase text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left">Type</th>
                <th className="px-4 py-2 text-right">Lines</th>
                <th className="px-4 py-2 text-right">Shortfall</th>
                <th className="px-4 py-2 text-right">Overcount</th>
              </tr>
            </thead>
            <tbody>
              {d.by_audit_type.map((r) => (
                <tr key={r.audit_type} className="border-t border-border">
                  <td className="px-4 py-2">{r.audit_type}</td>
                  <td className="px-4 py-2 text-right">{r.lines}</td>
                  <td className="px-4 py-2 text-right">{r.shortfall_units}</td>
                  <td className="px-4 py-2 text-right">{r.overcount_units}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>SKU leaders</CardTitle>
        </CardHeader>
        <CardBody className="overflow-x-auto p-0">
          {(d.sku_leaders.length ?? 0) === 0 ? (
            <EmptyState
              icon={<ScanLine className="h-5 w-5" />}
              title="No variance in range"
              description="No shortfall or overcount lines for this window."
            />
          ) : (
            <>
              <p className="px-4 pt-3 text-[11px] text-muted-foreground">
                Click a row for that SKU's full audit history, variance trend, and
                which packers counted it.
              </p>
              <table className="w-full text-xs">
                <thead className="bg-muted/40 text-[10px] uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2 text-left">Product</th>
                    <th className="px-4 py-2 text-left">Store</th>
                    <th className="px-4 py-2 text-right">Audits</th>
                    <th className="px-4 py-2 text-right">Shortfall</th>
                    <th className="px-4 py-2 text-right">Overcount</th>
                  </tr>
                </thead>
                <tbody>
                  {d.sku_leaders.map((r) => (
                    <DrillRow
                      key={`${r.product_id}-${r.store_id}`}
                      onOpen={() =>
                        openAuditDrilldown({
                          kind: 'sku',
                          productId: r.product_id,
                          productName: r.product_name,
                          storeId: r.store_id,
                        })
                      }
                    >
                      <td className="px-4 py-2">{r.product_name}</td>
                      <td className="px-4 py-2">{r.store_name}</td>
                      <td className="px-4 py-2 text-right">{r.audits}</td>
                      <td className="px-4 py-2 text-right">{r.shortfall_units}</td>
                      <td className="px-4 py-2 text-right">{r.overcount_units}</td>
                    </DrillRow>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Packers
// ---------------------------------------------------------------------------

function PackersTab({ filters }: TabProps) {
  const packers = useAuditsPackers(filters);

  if (packers.isLoading) return <Skeleton className="h-64" />;
  if (packers.isError || !packers.data) {
    return (
      <ErrorState
        description="Could not load packer accuracy."
        onRetry={() => void packers.refetch()}
      />
    );
  }

  return (
    <Card>
      <CardBody className="overflow-x-auto p-0">
        <p className="px-4 pt-3 text-[11px] text-muted-foreground">
          Click a packer for their sessions, worst SKUs, accuracy trend, and the
          lost-item reports they filed.
        </p>
        <table className="w-full text-xs">
          <thead className="bg-muted/40 text-[10px] uppercase text-muted-foreground">
            <tr>
              <th className="px-4 py-2 text-left">Packer</th>
              <th className="px-4 py-2 text-left">Store</th>
              <th className="px-4 py-2 text-right">Sessions</th>
              <th className="px-4 py-2 text-right">Lines</th>
              <th className="px-4 py-2 text-right">Match rate</th>
              <th className="px-4 py-2 text-right">Shortfall</th>
              <th className="px-4 py-2 text-right">Overcount</th>
            </tr>
          </thead>
          <tbody>
            {packers.data.rows.map((r) =>
              r.packer_id == null ? (
                <tr key={`unattributed-${r.store_id}`} className="border-t border-border">
                  <td className="px-4 py-2 text-muted-foreground">{r.packer_name}</td>
                  <td className="px-4 py-2">{r.store_name}</td>
                  <td className="px-4 py-2 text-right">{r.sessions}</td>
                  <td className="px-4 py-2 text-right">{r.lines}</td>
                  <td className="px-4 py-2 text-right">{pct(r.match_rate)}</td>
                  <td className="px-4 py-2 text-right">{r.shortfall_units}</td>
                  <td className="px-4 py-2 text-right">{r.overcount_units}</td>
                </tr>
              ) : (
                <DrillRow
                  key={`${r.packer_id}-${r.store_id}`}
                  onOpen={() =>
                    openAuditDrilldown({
                      kind: 'packer',
                      packerId: r.packer_id as number,
                      packerName: r.packer_name,
                    })
                  }
                >
                  <td className="px-4 py-2">{r.packer_name}</td>
                  <td className="px-4 py-2">{r.store_name}</td>
                  <td className="px-4 py-2 text-right">{r.sessions}</td>
                  <td className="px-4 py-2 text-right">{r.lines}</td>
                  <td className="px-4 py-2 text-right">{pct(r.match_rate)}</td>
                  <td className="px-4 py-2 text-right">{r.shortfall_units}</td>
                  <td className="px-4 py-2 text-right">{r.overcount_units}</td>
                </DrillRow>
              ),
            )}
          </tbody>
        </table>
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Coverage
// ---------------------------------------------------------------------------

function CoverageTab({ filters }: TabProps) {
  const coverage = useAuditsCoverage(filters);

  if (coverage.isLoading) return <Skeleton className="h-64" />;
  if (coverage.isError || !coverage.data) {
    return (
      <ErrorState
        description="Could not load coverage."
        onRetry={() => void coverage.refetch()}
      />
    );
  }

  const d = coverage.data;

  return (
    <div className="space-y-4">
      <KPICard
        label="Wrap events"
        value={fmt.number(d.wrap_events_in_range)}
        hint="Rotation wrapped in this window"
      />
      <Card>
        <CardHeader>
          <CardTitle>Rotation by store</CardTitle>
        </CardHeader>
        <CardBody className="overflow-x-auto p-0">
          <p className="px-4 pt-3 text-[11px] text-muted-foreground">
            Click a store to see exactly which SKUs were audited and which were
            never touched, ranked by stock value.
          </p>
          <table className="w-full text-xs">
            <thead className="bg-muted/40 text-[10px] uppercase text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left">Store</th>
                <th className="px-4 py-2 text-right">Round</th>
                <th className="px-4 py-2 text-right">In stock</th>
                <th className="px-4 py-2 text-right">Audited</th>
                <th className="px-4 py-2 text-right">Coverage</th>
              </tr>
            </thead>
            <tbody>
              {d.rotations.map((r) => (
                <DrillRow
                  key={r.store_id}
                  onOpen={() =>
                    openAuditDrilldown({
                      kind: 'coverageStore',
                      storeId: r.store_id,
                      storeName: r.store_name,
                    })
                  }
                >
                  <td className="px-4 py-2">{r.store_name}</td>
                  <td className="px-4 py-2 text-right">{r.current_round}</td>
                  <td className="px-4 py-2 text-right">{r.skus_in_stock}</td>
                  <td className="px-4 py-2 text-right">{r.skus_audited_in_range}</td>
                  <td className="px-4 py-2 text-right">{pct(r.coverage_rate)}</td>
                </DrillRow>
              ))}
            </tbody>
          </table>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Never audited</CardTitle>
        </CardHeader>
        <CardBody className="overflow-x-auto p-0">
          {d.never_audited_sample.length === 0 ? (
            <EmptyState
              icon={<ScanLine className="h-5 w-5" />}
              title="Nothing untouched"
              description="Every in-stock SKU has been audited in this window."
            />
          ) : (
            <>
              <p className="px-4 pt-3 text-[11px] text-muted-foreground">
                A sample of in-stock SKUs with no audit line in this window — these
                are the shelves nobody has counted. Click a row for that SKU's
                history; click through from Rotation for a store's full list.
              </p>
              <table className="w-full text-xs">
                <thead className="bg-muted/40 text-[10px] uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2 text-left">Product</th>
                    <th className="px-4 py-2 text-left">Store</th>
                    <th className="px-4 py-2 text-right">In stock</th>
                  </tr>
                </thead>
                <tbody>
                  {d.never_audited_sample.map((r) => (
                    <DrillRow
                      key={`${r.store_id}-${r.product_id}`}
                      onOpen={() =>
                        openAuditDrilldown({
                          kind: 'sku',
                          productId: r.product_id,
                          productName: r.product_name,
                          storeId: r.store_id,
                        })
                      }
                    >
                      <td className="px-4 py-2">{r.product_name}</td>
                      <td className="px-4 py-2">{r.store_name}</td>
                      <td className="px-4 py-2 text-right">{r.stock_quantity}</td>
                    </DrillRow>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sessions
// ---------------------------------------------------------------------------

function SessionsTab({ filters }: TabProps) {
  const [page, setPage] = useState(1);
  const sessions = useAuditsSessions({ ...filters, page, pageSize: 25 });

  if (sessions.isLoading) return <Skeleton className="h-64" />;
  if (sessions.isError || !sessions.data) {
    return (
      <ErrorState
        description="Could not load sessions."
        onRetry={() => void sessions.refetch()}
      />
    );
  }

  const { results, pagination } = sessions.data;

  return (
    <Card>
      <CardBody className="overflow-x-auto p-0">
        <p className="px-4 pt-3 text-[11px] text-muted-foreground">
          Click a session to see its line items with the signed variance per line.
        </p>
        <table className="w-full text-xs">
          <thead className="bg-muted/40 text-[10px] uppercase text-muted-foreground">
            <tr>
              <th className="px-4 py-2 text-left">Audit</th>
              <th className="px-4 py-2 text-left">Store</th>
              <th className="px-4 py-2 text-left">Packer</th>
              <th className="px-4 py-2 text-right">Items</th>
              <th className="px-4 py-2 text-right">Shortfall</th>
              <th className="px-4 py-2 text-right">Overcount</th>
              <th className="px-4 py-2 text-right">Started</th>
            </tr>
          </thead>
          <tbody>
            {results.map((r) => (
              <DrillRow
                key={r.id}
                onOpen={() => openAuditDrilldown({ kind: 'session', auditId: r.id })}
              >
                <td className="px-4 py-2">#{r.id}</td>
                <td className="px-4 py-2">{r.store_name}</td>
                <td className="px-4 py-2">{r.packer_name}</td>
                <td className="px-4 py-2 text-right">{r.item_count}</td>
                <td className="px-4 py-2 text-right">{r.shortfall_units}</td>
                <td className="px-4 py-2 text-right">{r.overcount_units}</td>
                <td className="px-4 py-2 text-right text-muted-foreground">
                  {r.created_at ? fmt.ago(r.created_at) : '—'}
                </td>
              </DrillRow>
            ))}
          </tbody>
        </table>
        {pagination.total_pages > 1 ? (
          <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-2 text-xs">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="press rounded-md border border-border px-2 py-1 disabled:opacity-50"
            >
              Prev
            </button>
            <span>
              {page} / {pagination.total_pages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
              disabled={page >= pagination.total_pages}
              className="press rounded-md border border-border px-2 py-1 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}
